import type { MPARequest } from '@repo/api-gw-sdk';

import type { ProjectIngestionPipeline } from './projectStreams';

const INGESTION_PIPELINE_PATH =
  /\/lakehouse\/workspace\/([^/?]+)\/ingestion-pipeline\/([^/?]+)/;

export const getIngestionPipelineTarget = (
  request: MPARequest | null | undefined
): { workspaceId: string; pipelineId: string } | null => {
  const match = INGESTION_PIPELINE_PATH.exec(request?.requestPath ?? '');
  if (!match?.[1] || !match[2]) {
    return null;
  }
  return { workspaceId: match[1], pipelineId: match[2] };
};

// Stream names are unique only within a workspace, so the workspace is named
// once the streams span more than one.
const streamLabels = (streams: ProjectIngestionPipeline[]) => {
  const workspacesWithStreams = new Set(
    streams.map(({ workspace }) => workspace.id)
  ).size;
  return new Map(
    streams.map(({ workspace, pipeline }) => [
      pipeline.id,
      workspacesWithStreams > 1
        ? `${pipeline.name} (${workspace.name})`
        : pipeline.name,
    ])
  );
};

// A selected stream that no longer exists stays listed under its ID, so the
// user can see it and take it out of the rule.
export const streamPickerOptions = (
  streams: ProjectIngestionPipeline[],
  selectedIds: string[]
) => {
  const labels = streamLabels(streams);
  const existing = [...labels]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const missing = selectedIds
    .filter((id) => !labels.has(id))
    .map((id) => ({ id, name: id }));
  return [...existing, ...missing];
};

export const selectedStreamLabels = (
  selectedIds: string[],
  streams: ProjectIngestionPipeline[]
) => {
  const labels = streamLabels(streams);
  return selectedIds.map((id) => ({
    id,
    label: labels.get(id) ?? id,
    exists: labels.has(id),
  }));
};
